import { useState, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth.js';
import ProtectedRoute from './components/ProtectedRoute.js';
import DashboardLayout from './components/DashboardLayout.js';
import OnboardingWizard from './components/OnboardingWizard.js';
import LoginPage from './pages/LoginPage.js';
import SignUpPage from './pages/SignUpPage.js';
import ForgotPasswordPage from './pages/ForgotPasswordPage.js';
import ResetPasswordPage from './pages/ResetPasswordPage.js';
import DashboardPage from './pages/DashboardPage.js';
import PipelinesPage from './pages/PipelinesPage.js';
import ContentQueuePage from './pages/ContentQueuePage.js';
import PlatformConnectionsPage from './pages/PlatformConnectionsPage.js';
import SettingsPage from './pages/SettingsPage.js';
import LandingPage from './pages/LandingPage.js';
import TermsPage from './pages/TermsPage.js';
import PrivacyPage from './pages/PrivacyPage.js';

function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    const completed = user.user_metadata?.['onboarding_completed'] === true;
    setShowOnboarding(!completed);
  }, [user, loading]);

  function handleOnboardingComplete() {
    setShowOnboarding(false);
    navigate('/dashboard/content-queue', { replace: true });
  }

  return (
    <>
      {showOnboarding && <OnboardingWizard onComplete={handleOnboardingComplete} />}
      {children}
    </>
  );
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }
  return user ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <OnboardingGate>
                  <DashboardLayout />
                </OnboardingGate>
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="pipelines" element={<PipelinesPage />} />
            <Route path="content-queue" element={<ContentQueuePage />} />
            <Route path="connections" element={<PlatformConnectionsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-white">404 Not Found</h1>
      </div>
    </div>
  );
}
